/** Shown when email/phone or password does not match (never reveal which). */
export const LOGIN_INVALID_CREDENTIALS_MSG = 'Incorrect username or password'

/** Maps API field paths to form field names used in the UI. */
const FIELD_ALIASES = {
  companyName: 'organization',
  'companyLocation.country': 'country',
  'companyLocation.address': 'address'
}

export const mapEmploymeFieldName = (apiField) => FIELD_ALIASES[apiField] || apiField

export const getEmploymeResponseMessage = (responseBody) => {
  if (!responseBody) return ''
  const { data, message } = responseBody
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data.message === 'string' && data.message.trim()) return data.message
  if (typeof message === 'string' && message !== 'error' && message !== 'success' && message.trim()) {
    return message
  }
  return ''
}

export const getEmploymeFieldErrors = (responseBody) => {
  const errors = responseBody?.errors
  if (!Array.isArray(errors) || !errors.length) return []
  return errors.map((e) => ({
    field: mapEmploymeFieldName(e.field),
    msg: e.msg
  }))
}

/**
 * @param {{ error: string, result?: { status?: number, data?: object } }} requestResult
 */
export const parseEmploymeRequestResult = (requestResult) => {
  const res = requestResult?.result
  const body = res?.data
  const status = res?.status
  const message = getEmploymeResponseMessage(body)
  const fieldErrors = getEmploymeFieldErrors(body)
  const ok = requestResult?.error === '' && res && status >= 200 && status < 300
  return { ok, status, message, fieldErrors, body }
}

export const applyFieldErrors = (currentErrors, fieldErrors) => {
  if (!fieldErrors?.length) return currentErrors
  const updated = currentErrors.map((e) => ({ ...e, msg: '' }))
  for (const fe of fieldErrors) {
    const idx = updated.findIndex((e) => e.field === fe.field)
    if (idx !== -1) {
      updated[idx].msg = fe.msg
    }
  }
  return updated
}

const GENERIC_SERVER_PHRASES = [
  'internal server error',
  'something went wrong',
  'an error occurred'
]

const isUsableServerMessage = (msg) => {
  if (!msg || typeof msg !== 'string') return false
  const lower = msg.trim().toLowerCase()
  if (!lower) return false
  return !GENERIC_SERVER_PHRASES.some((p) => lower === p || lower.startsWith(p))
}

/**
 * User-friendly signup error from API status + body.
 * @param {{ status?: number, message?: string, fieldErrors?: Array, role?: 'employer'|'job_seeker' }} opts
 */
export const resolveSignupErrorMessage = ({ status, message = '', fieldErrors = [], role = 'job_seeker' }) => {
  if (fieldErrors.length) {
    if (isUsableServerMessage(message)) return message
    return 'Please correct the highlighted fields and try again.'
  }

  if (isUsableServerMessage(message)) return message

  if (status === 409) {
    return role === 'employer'
      ? 'An employer account with this email or phone number already exists. Sign in instead or use different details.'
      : 'An account with this email or phone number already exists. Sign in instead or use different details.'
  }

  if (status === 400) {
    return 'Some details on the form are invalid. Review the fields marked in red and try again.'
  }

  if (status === 422) {
    return 'Please check the form and fix any invalid fields before submitting again.'
  }

  if (status === 503) {
    return message ||
      'Your account may have been created, but we could not send the verification email. Try signing in or contact support for help.'
  }

  if (status === 500) {
    return 'We could not finish creating your account due to a server problem. Please wait a moment and try again.'
  }

  if (status === 502 || status === 504) {
    return 'The server took too long to respond. Check your connection and try again.'
  }

  if (!status || status === 0) {
    return 'We could not reach the server. Check your internet connection and try again.'
  }

  return role === 'employer'
    ? 'We could not create your employer account. Please try again. If the problem continues, contact support.'
    : 'We could not create your account. Please try again. If the problem continues, contact support.'
}

/**
 * User-friendly login error (non credential failures).
 */
export const resolveLoginErrorMessage = ({ status, message = '', fieldErrors = [] }) => {
  if (fieldErrors.length) {
    if (isUsableServerMessage(message)) return message
    return 'Please enter a valid email or phone number and password.'
  }

  if (isUsableServerMessage(message)) return message

  if (status === 400) {
    return 'Please enter your email or phone number and password.'
  }

  if (status === 403) {
    return message || 'You cannot sign in with this account right now. Contact support if you need help.'
  }

  if (status === 500) {
    return 'Sign-in is temporarily unavailable. Please try again in a few minutes.'
  }

  if (!status || status === 0) {
    return 'We could not reach the server. Check your internet connection and try again.'
  }

  return 'We could not sign you in. Please try again.'
}
